import type { EChartsOption } from 'echarts'
import {
  axisTextStyle,
  deltaColor,
  formatCompactNumber,
  formatSignedShare,
  isFiniteNumber,
  relativeDelta,
  tooltipBase,
  tooltipHtml,
} from './format'
import { rampColor, seriesColor, type ChartTheme } from './palette'

export interface DumbbellRow {
  key: string
  label: string
  sublabel?: string | null
  /** «До» (прошлый период, до события); `null` — нет данных. */
  from: number | null
  /** «После». */
  to: number | null
}

export interface DumbbellInput {
  rows: DumbbellRow[]
  fromLabel: string
  toLabel: string
  valueFormat?: (value: number) => string
  /** Подпись Δ справа от строки (по умолчанию — да). */
  showDelta?: boolean
  /** Рост — плохо. */
  invertDelta?: boolean
  rowLabelWidth?: number
}

/** Цвета точек: одна гамма, две ступени — «до» светлее, «после» насыщенный слот 1. */
export function dumbbellColors(theme: ChartTheme): { from: string; to: string } {
  return { from: rampColor(theme.sequential, theme.mode === 'dark' ? 0.45 : 0.3), to: seriesColor(theme, 0) }
}

export function dumbbellHeight(rows: number): number {
  return Math.max(96, rows * 30 + 40)
}

/**
 * «Было → стало» по строкам: две точки на одной оси, соединённые тонкой линией. Ось от нуля, одна шкала
 * на все строки. Справа — Δ подписью цветом текста роста/падения. Точки 10 px с кольцом цвета поверхности;
 * область клика — вся строка (custom-серия на строку).
 */
export function buildDumbbellOption(input: DumbbellInput, theme: ChartTheme): EChartsOption {
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const colors = dumbbellColors(theme)
  const showDelta = input.showDelta ?? true
  const values = input.rows.flatMap((row) => [row.from, row.to]).filter(isFiniteNumber)
  const max = values.length ? Math.max(...values) : 1

  // Пустое значение — отрицательная метка (значения «до/после» неотрицательны): строки с NaN custom-серия пропускает целиком.
  const data = input.rows.map((row, index) => [index, isFiniteNumber(row.from) ? row.from : -1, isFiniteNumber(row.to) ? row.to : -1])

  return {
    backgroundColor: 'transparent',
    animation: false,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    grid: { top: 24, left: 8, right: showDelta ? 72 : 16, bottom: 4, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        const row = input.rows[params.dataIndex]
        if (!row) return ''
        const delta = relativeDelta(row.to, row.from)
        return tooltipHtml(theme, row.sublabel ? `${row.label} · ${row.sublabel}` : row.label, [
          { color: colors.from, label: input.fromLabel, value: isFiniteNumber(row.from) ? valueFormat(row.from) : '—' },
          {
            color: colors.to,
            label: input.toLabel,
            value: isFiniteNumber(row.to) ? valueFormat(row.to) : '—',
            note: delta !== null ? formatSignedShare(delta) : undefined,
            noteColor: deltaColor(theme, delta, input.invertDelta),
          },
        ])
      },
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: max > 0 ? undefined : 1,
      position: 'top',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), formatter: (value: number) => formatCompactNumber(value) },
      splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: input.rows.map((_row, index) => `r${index}`),
      triggerEvent: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...axisTextStyle(theme),
        color: theme.textSecondary,
        fontSize: 12,
        width: input.rowLabelWidth ?? 180,
        overflow: 'truncate',
        interval: 0,
        formatter: (key: string) => input.rows[Number(String(key).slice(1))]?.label ?? '',
      },
    },
    series: [
      {
        type: 'custom',
        cursor: 'pointer',
        encode: { x: [1, 2], y: 0 },
        data,
        renderItem: (params: any, api: any) => {
          const rowIndex = api.value(0)
          const from = api.value(1)
          const to = api.value(2)
          const row = input.rows[rowIndex]
          const center = api.coord([0, rowIndex])
          const bandHeight = api.size([0, 1])[1]
          const coordSys = params.coordSys
          const children: any[] = [
            // Невидимая полоса строки — область наведения и клика больше метки.
            {
              type: 'rect',
              shape: { x: coordSys.x, y: center[1] - bandHeight / 2, width: coordSys.width, height: bandHeight },
              style: { fill: 'rgba(0,0,0,0)' },
            },
          ]
          const fromPoint = from >= 0 ? api.coord([from, rowIndex]) : null
          const toPoint = to >= 0 ? api.coord([to, rowIndex]) : null
          if (fromPoint && toPoint) {
            children.push({
              type: 'line',
              shape: { x1: fromPoint[0], y1: fromPoint[1], x2: toPoint[0], y2: toPoint[1] },
              style: { stroke: theme.axis, lineWidth: 2 },
            })
          }
          if (fromPoint) {
            children.push({
              type: 'circle',
              shape: { cx: fromPoint[0], cy: fromPoint[1], r: 5 },
              style: { fill: colors.from, stroke: theme.surface, lineWidth: 2 },
            })
          }
          if (toPoint) {
            children.push({
              type: 'circle',
              shape: { cx: toPoint[0], cy: toPoint[1], r: 5 },
              style: { fill: colors.to, stroke: theme.surface, lineWidth: 2 },
            })
          }
          if (showDelta && row) {
            const delta = relativeDelta(row.to, row.from)
            children.push({
              type: 'text',
              style: {
                x: coordSys.x + coordSys.width + 8,
                y: center[1],
                text: delta === null ? '—' : formatSignedShare(delta),
                fill: deltaColor(theme, delta, input.invertDelta) ?? theme.textMuted,
                font: `11px ${theme.fontFamily}`,
                verticalAlign: 'middle',
              },
            })
          }
          return { type: 'group', children }
        },
      },
    ],
  } as EChartsOption
}
